import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Download, Upload, ArrowLeft, CheckCircle2, XCircle, PackagePlus, Loader2 } from "lucide-react";

const UNITS = ["kg", "g", "pcs", "dozen", "box"];

export default function BulkImportPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/products/bulk-import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "bakemill_product_import_template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error("Failed to download template");
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    setParsing(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/products/bulk-import/parse", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRows(data.rows);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to parse file");
      setRows(null);
    } finally {
      setParsing(false);
    }
  };

  const revalidateRow = (row, changedField) => {
    const errors = {};
    if (!row.name?.trim()) errors.name = "Product Name is required";
    if (!row.category?.trim()) errors.category = "Category is required";
    if (!row.sku?.trim()) errors.sku = "SKU is required";
    else if (row.errors?.sku && changedField !== "sku") errors.sku = row.errors.sku;
    if (row.errors?.barcode && changedField !== "barcode") errors.barcode = row.errors.barcode;
    if (row.mrp === "" || row.mrp === null || isNaN(Number(row.mrp)) || Number(row.mrp) <= 0) errors.mrp = "MRP must be greater than 0";
    if (row.sale_price === "" || row.sale_price === null || isNaN(Number(row.sale_price)) || Number(row.sale_price) <= 0) errors.sale_price = "Sale Price must be greater than 0";
    if (row.gst_rate === "" || row.gst_rate === null || isNaN(Number(row.gst_rate)) || Number(row.gst_rate) < 0 || Number(row.gst_rate) > 28) errors.gst_rate = "GST % must be 0-28";
    if (!UNITS.includes(row.unit)) errors.unit = "Pick a valid unit";
    if (row.initial_stock !== "" && row.initial_stock !== null && (isNaN(Number(row.initial_stock)) || Number(row.initial_stock) < 0)) errors.initial_stock = "Initial HO Stock cannot be negative";
    const hasMfg = !!row.mfg_date, hasExp = !!row.expiry_date;
    if (hasMfg !== hasExp) errors[hasMfg ? "expiry_date" : "mfg_date"] = "Provide both Mfg and Expiry dates (or neither)";
    else if (hasMfg && row.expiry_date < row.mfg_date) errors.expiry_date = "Expiry Date must be after Mfg Date";
    return { ...row, errors, valid: Object.keys(errors).length === 0 };
  };

  const updateCell = (rowNumber, field, value) => {
    setRows((prev) =>
      prev.map((r) => (r.row_number === rowNumber ? revalidateRow({ ...r, [field]: value }, field) : r))
    );
  };

  const validRows = rows ? rows.filter((r) => r.valid) : [];
  const invalidCount = rows ? rows.length - validRows.length : 0;

  const importAll = async () => {
    if (!validRows.length) return toast.error("No valid rows to import");
    setImporting(true);
    try {
      const payload = {
        rows: validRows.map((r) => ({
          row_number: r.row_number,
          name: r.name,
          category: r.category,
          sku: r.sku,
          barcode: r.barcode || null,
          mrp: Number(r.mrp),
          sale_price: Number(r.sale_price),
          gst_rate: Number(r.gst_rate),
          unit: r.unit,
          initial_stock: Number(r.initial_stock) || 0,
          mfg_date: r.mfg_date || null,
          expiry_date: r.expiry_date || null,
        })),
      };
      const { data } = await api.post("/products/bulk-import/commit", payload);
      setResult(data);
      toast.success(`Imported ${data.created_count} product(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setRows(null);
    setResult(null);
    setFileName("");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-6" data-testid="bulk-import-page">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => navigate("/app/products")} data-testid="bulk-import-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Products
        </Button>
      </div>
      <div>
        <div className="uc-label">Catalog</div>
        <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Bulk Product Importer</h1>
        <p className="text-sm text-slate-600 mt-1">Download the template, fill it in, upload it, fix any highlighted rows, then import.</p>
      </div>

      <div className="border border-slate-200 rounded-lg bg-white p-6 space-y-4" data-testid="bulk-import-step1">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#C05621]">
          <span className="w-6 h-6 rounded-full bg-[#C05621] text-white flex items-center justify-center text-xs">1</span>
          Download the template
        </div>
        <Button onClick={downloadTemplate} variant="outline" data-testid="bulk-import-download-template-btn">
          <Download className="w-4 h-4 mr-1.5" /> Download Excel Template
        </Button>
        <p className="text-xs text-slate-500">Columns: Product Name, Category, SKU, Barcode, MRP (Rs), Sale Price (Rs), GST %, Unit (kg/g/pcs/dozen/box), <b>Initial HO Stock</b>, Mfg Date, Expiry Date</p>
        <p className="text-xs text-slate-500" data-testid="bulk-import-stock-hint">Initial HO Stock is credited to the HO Master Warehouse on import — instantly available in Dispatch and the Products Stock column. Add Mfg + Expiry dates to register it as a FEFO batch.</p>
      </div>

      <div className="border border-slate-200 rounded-lg bg-white p-6 space-y-4" data-testid="bulk-import-step2">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#C05621]">
          <span className="w-6 h-6 rounded-full bg-[#C05621] text-white flex items-center justify-center text-xs">2</span>
          Upload your completed file
        </div>
        <div className="flex items-center gap-3">
          <Input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFile}
            className="max-w-md"
            data-testid="bulk-import-file-input"
          />
          {parsing && <Loader2 className="w-4 h-4 animate-spin text-[#C05621]" />}
          {fileName && !parsing && <span className="text-xs text-slate-500">{fileName}</span>}
        </div>
      </div>

      {rows && !result && (
        <div className="border border-slate-200 rounded-lg bg-white p-6 space-y-4" data-testid="bulk-import-step3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#C05621]">
              <span className="w-6 h-6 rounded-full bg-[#C05621] text-white flex items-center justify-center text-xs">3</span>
              Preview &amp; fix ({rows.length} rows)
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1 text-emerald-700" data-testid="bulk-import-valid-count"><CheckCircle2 className="w-3.5 h-3.5" /> {validRows.length} ready</span>
              {invalidCount > 0 && <span className="flex items-center gap-1 text-red-600" data-testid="bulk-import-invalid-count"><XCircle className="w-3.5 h-3.5" /> {invalidCount} need fixing</span>}
            </div>
          </div>

          <div className="overflow-x-auto max-h-[520px] overflow-y-auto border border-slate-100 rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">#</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Product Name</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Category</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">SKU</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Barcode</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">MRP</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Sale Price</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">GST %</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Unit</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Initial HO Stock</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Mfg Date</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Expiry Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.row_number} className={r.valid ? "" : "bg-red-50/60"} data-testid={`bulk-import-row-${r.row_number}`}>
                    <td className="px-3 py-1.5 text-slate-400">{r.row_number}</td>
                    <RowCell row={r} field="name" onChange={updateCell} />
                    <RowCell row={r} field="category" onChange={updateCell} />
                    <RowCell row={r} field="sku" onChange={updateCell} />
                    <RowCell row={r} field="barcode" onChange={updateCell} />
                    <RowCell row={r} field="mrp" type="number" onChange={updateCell} />
                    <RowCell row={r} field="sale_price" type="number" onChange={updateCell} />
                    <RowCell row={r} field="gst_rate" type="number" onChange={updateCell} />
                    <RowCell row={r} field="unit" select onChange={updateCell} />
                    <RowCell row={r} field="initial_stock" type="number" onChange={updateCell} />
                    <RowCell row={r} field="mfg_date" type="date" onChange={updateCell} />
                    <RowCell row={r} field="expiry_date" type="date" onChange={updateCell} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={importAll}
              disabled={!validRows.length || importing}
              className="bg-[#C05621] hover:bg-[#9C4221] text-white"
              data-testid="bulk-import-run-btn"
            >
              {importing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Importing…</> : <><PackagePlus className="w-4 h-4 mr-1.5" /> Import All ({validRows.length})</>}
            </Button>
            <Button variant="outline" onClick={reset} data-testid="bulk-import-reset-btn">Start Over</Button>
          </div>
        </div>
      )}

      {result && (
        <div className="border-2 border-emerald-200 rounded-lg bg-emerald-50/50 p-6 space-y-3" data-testid="bulk-import-result">
          <div className="flex items-center gap-2 text-emerald-900 font-[Outfit] text-lg font-semibold">
            <CheckCircle2 className="w-5 h-5" /> Imported {result.created_count} product(s)
          </div>
          {result.stocked_count > 0 && (
            <div className="text-sm text-emerald-800" data-testid="bulk-import-stock-summary">
              {result.stocked_count} product(s) seeded with {Number(result.stocked_qty).toLocaleString("en-IN")} units of opening stock at {result.warehouse_name || "HO Warehouse"} — ready to dispatch.
            </div>
          )}
          {result.skipped?.length > 0 && (
            <div className="text-sm text-red-700 space-y-1" data-testid="bulk-import-skipped-list">
              <div>{result.skipped.length} row(s) were skipped:</div>
              <ul className="text-xs list-disc pl-5 space-y-0.5">
                {result.skipped.map((s) => (
                  <li key={s.row_number}>
                    Row {s.row_number} ({s.sku || "no SKU"}): {Object.values(s.errors || {}).join(", ")}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex gap-3">
            <Button onClick={() => navigate("/app/products")} className="bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="bulk-import-done-btn">
              Go to Products
            </Button>
            <Button variant="outline" onClick={reset} data-testid="bulk-import-another-btn">Import Another File</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function RowCell({ row, field, onChange, type = "text", select = false }) {
  const error = row.errors?.[field];
  const value = row[field];
  const base = "w-full bg-transparent border rounded px-2 py-1 text-sm focus:outline-none";
  const cls = error ? `${base} border-red-400 bg-red-50 text-red-900` : `${base} border-transparent focus:border-slate-300`;
  return (
    <td className="px-3 py-1.5 min-w-[110px]" title={error || ""}>
      {select ? (
        <select
          className={cls}
          value={value || ""}
          onChange={(e) => onChange(row.row_number, field, e.target.value)}
          data-testid={`bulk-import-cell-${field}-${row.row_number}`}
        >
          <option value="">--</option>
          {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
      ) : (
        <input
          className={cls}
          type={type}
          value={value === null || value === undefined ? "" : value}
          onChange={(e) => onChange(row.row_number, field, type === "number" ? e.target.value : e.target.value)}
          data-testid={`bulk-import-cell-${field}-${row.row_number}`}
        />
      )}
    </td>
  );
}
